import { YAxisOptions } from 'highcharts/highcharts';
import { WidgetType } from 'types/widget.types';
import { isDevelopment } from 'utils/environment';
import { isFiniteNumber, isNumberInRange, parseFiniteNumberWithNaN } from 'utils/number';

import { formatDataLabel, replaceGWithBFormatter } from '../ChartWidget.helpers';
import { HighchartsSeriesData } from '../ChartWidget.hooks';
import { HighchartsOptions } from '../DataTableChartCommon/DataTableChartCommon.types';
import { DEFAULT_HEIGHT, DEFAULT_HISTOGRAM_CHART_DATA, DEFAULT_WIDTH } from './HistogramChartWidget.config';
import { VERSION } from './HistogramChartWidget.upgrade';
import { HistogramChartWidgetData } from './histogramChartWidget.types';

export const generateDefaultData = () => {
  return {
    widgetType: WidgetType.HistogramChart,
    widgetData: {
      version: VERSION,
      widthPx: DEFAULT_WIDTH,
      heightPx: DEFAULT_HEIGHT,
      topPx: 0,
      leftPx: 0,
      rotateDeg: 0,
      altText: '',
      isDecorative: false,
      patterns: {
        enabled: false,
        list: [],
      },
      // Histogram chart specific data
      ...DEFAULT_HISTOGRAM_CHART_DATA,
    } as HistogramChartWidgetData,
  };
};

export const getHighChartHistogramConfig = (
  defaultOptions: HighchartsOptions,
  widgetData: HistogramChartWidgetData,
  series: HighchartsSeriesData[],
  keyCustom: number,
) => {
  const options = defaultOptions;
  const {
    dataLabels: { prefix, suffix },
    generalOptions: { binSize },
    numberFormat: { precision, separator } = {},
    seriesMeta: { minXValue, maxXValue },
    xAxis: { enablelabels: enableXlabels = true, labelStyle: xLabelStyle },
  } = widgetData;

  options.chart = options.chart || {};
  options.chart.type = 'column';

  options.keyCustom = keyCustom;

  const minValue = parseFiniteNumberWithNaN(minXValue);
  const maxValue = parseFiniteNumberWithNaN(maxXValue);

  // For now, we only take the first column of the data to generate the histogram
  let processData: number[] = [];
  if (Array.isArray(series?.[0].data) && series[0].data.length && typeof series[0].data[0] === 'number') {
    processData = series[0].data as number[];
  } else if (isDevelopment) {
    throw new Error('Histogram chart data is not valid');
  }

  const binWidth = isFiniteNumber(binSize) ? binSize : undefined;
  const { histogramData, lineData } = calculateHistogramData(processData, minValue, maxValue, binWidth);

  options.series = [
    {
      visible: true,
      type: 'column',
      groupPadding: 0,
      pointPadding: 0,
      data: histogramData,
      color: widgetData.seriesMeta.colors[0],
      dataLabels: { format: '{y}' },
    },
    {
      visible: true,
      color: 'transparent',
      dataLabels: { enabled: false },
      type: 'line',
      data: lineData,
      xAxis: 1,
    },
  ];

  options.yAxis = [
    {
      ...(defaultOptions.yAxis as YAxisOptions),
      labels: {
        ...((defaultOptions.yAxis as YAxisOptions)?.labels || {}),
        formatter: function () {
          return replaceGWithBFormatter.call(this);
        },
      },
    },
  ];

  options.xAxis = [
    {
      labels: { enabled: false },
      tickLength: 0,
      title: {
        text: undefined,
      },
    },
    {
      ...defaultOptions.xAxis,
      type: 'category',
      showFirstLabel: true,
      showLastLabel: true,
      tickPositions: lineData.map((item) => Number(item.x.toFixed(2))),
      labels: {
        enabled: enableXlabels,
        style: xLabelStyle,
        formatter: function () {
          return formatDataLabel({ dataLabel: this.value, prefix, suffix, precision, separator });
        },
      },
    },
  ];

  options.plotOptions = options.plotOptions ?? {};

  return options;
};

/**
 * Distribute the data into bins and calculate the number of data points in each bin
 *
 * @param data
 * @param minValue // The lower bound of the x axis
 * @param maxValue // The upper bound of the x axis
 * @param binSize // The width of each bin
 * @returns
 */
export const calculateHistogramData = (data: number[], minValue: number, maxValue: number, binWidth?: number) => {
  const sortedData = data.filter((value) => isNumberInRange(minValue, maxValue, value)).sort((a, b) => a - b);

  if (!sortedData.length) {
    return {
      sortedData: [],
      histogramData: [],
      lineData: [],
    };
  }

  const min = isFiniteNumber(minValue) ? minValue : sortedData[0];
  const max = isFiniteNumber(maxValue) ? maxValue : sortedData[sortedData.length - 1] + 1;
  const binSize = isFiniteNumber(binWidth) && binWidth <= 0 ? undefined : binWidth;

  // If the binSize is set by the user, we calculate the number of bins based on the binSize
  // Otherwise, we calculate the number of bins based on the square root of the number of data points
  const binsNumber = binSize ? Math.ceil((max - min) / binSize) : Math.ceil(Math.sqrt(data.length));
  const interval = binSize ?? (max - min) / binsNumber;

  // Distribute the data into bins
  // Example: if the data is [1, 2, 3, 4, 5, 6, 7, 8, 9] and the number of bins is 3
  // The data will be distributed into 3 bins: [1, 2, 3], [4, 5, 6], [7, 8, 9]
  const histogramData = sortedData.reduce((acc, value) => {
    acc[Math.floor((value - min) / interval)]++;
    return acc;
  }, Array(binsNumber).fill(0) as number[]);

  // Calculate the break points of bins, which contains the edges of the bins so +1 is needed
  // Example: if the data is [1, 2, 3, 4, 5, 6, 7, 8, 9] and the number of bins is 3
  // The break points will be [1, 4, 7, 10]
  const breakPoints = Array(binsNumber + 1)
    .fill(0)
    .map((_, index) => min + interval * index);

  // We need to draw a line chart on top of the histogram chart to show the break points of the histogram
  // Meanwhile, we want to add prefix and the suffix to the axis labels, so we need this Highcharts line chart data
  // The y value below is arbitrary, we just need a number to make it work and the chart would be transparent
  const lineData = breakPoints.map((breakPoint) => ({ x: breakPoint, y: 1 }));

  return {
    sortedData,
    histogramData,
    lineData,
  };
};
